import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, Bell, BellRing, BellOff, Sun, Moon, Monitor, User2, LogOut, ChevronDown, Palette, RotateCcw, AlertTriangle, Info, Map } from 'lucide-react'
import { HexColorPicker, HexColorInput } from 'react-colorful'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import { useTenantStore } from '@/store/tenant'
import { toast } from '@/store/toast'
import { acquireAndStoreToken, requestPushPermission, useNotificationPermission } from '@/hooks/usePushNotifications'
import { MAP_TILES, MAP_LAYER_KEYS, type MapLayerKey } from '@/lib/mapTiles'
import { listAlerts } from '@/api/alerts'
import { cn } from '@/utils/cn'
import api from '@/lib/api'

type ColorMode = 'light' | 'dark' | 'system'

const COLOR_MODE_ICONS: Record<ColorMode, React.ElementType> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
}

/** Poll unread alert count every 30 s */
function useUnreadAlertCount() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  return useQuery({
    queryKey: ['alerts', 'unread-count'],
    queryFn: () =>
      api
        .get<{ success: true; meta: { total: number } }>('/alerts', {
          params: { is_read: false, limit: 1 },
        })
        .then((r) => r.data.meta.total),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    enabled: isAuthenticated,
  })
}

export function Header() {
  const { t, i18n } = useTranslation()
  const toggleSidebar = useUIStore((s) => s.toggleSidebar)
  const colorMode = useUIStore((s) => s.colorMode)
  const setColorMode = useUIStore((s) => s.setColorMode)
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)

  const setPrimaryColor = useTenantStore((s) => s.setPrimaryColor)
  const currentPrimary  = useTenantStore((s) => s.theme?.primaryColor ?? '#53C9C0')

  const navigate = useNavigate()

  const mapLayer    = useUIStore((s) => s.mapLayer)
  const setMapLayer = useUIStore((s) => s.setMapLayer)

  const [userMenuOpen,    setUserMenuOpen]    = useState(false)
  const [colorMenuOpen,   setColorMenuOpen]   = useState(false)
  const [mapMenuOpen,     setMapMenuOpen]     = useState(false)
  const [alertsOpen,      setAlertsOpen]      = useState(false)
  const [themePickerOpen, setThemePickerOpen] = useState(false)
  const [pickerColor,     setPickerColor]     = useState('#53C9C0')

  const DEFAULT_COLOR = '#53C9C0'

  const openThemePicker = () => {
    setPickerColor(currentPrimary)
    setThemePickerOpen(true)
  }
  const applyThemeColor = () => {
    setPrimaryColor(pickerColor)
    setThemePickerOpen(false)
  }
  const resetThemeColor = () => {
    setPrimaryColor(DEFAULT_COLOR)
    setPickerColor(DEFAULT_COLOR)
    setThemePickerOpen(false)
  }

  const notifPerm   = useNotificationPermission()
  const [notifBusy, setNotifBusy] = useState(false)

  const handleEnableNotifications = async () => {
    if (notifPerm === 'denied' || notifBusy) return
    setNotifBusy(true)
    try {
      if (notifPerm === 'granted') {
        // Permission already granted — retry token registration (e.g. after SW error)
        await acquireAndStoreToken()
        toast.success('Push notifications are active')
      } else {
        const result = await requestPushPermission()
        if (result === 'granted') toast.success('Push notifications enabled')
        else if (result === 'denied') toast.error('Push notifications blocked in browser settings')
      }
    } catch (err) {
      console.warn('[FCM] Bell button action failed:', err)
      toast.error('Failed to enable push notifications')
    }
    setNotifBusy(false)
  }

  // Quick test — fires a local notification to verify browser + SW are working
  const handleTestNotification = async () => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return
    const reg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')
    if (reg) {
      reg.showNotification('Test Notification', {
        body: 'Push notifications are working correctly.',
        icon: '/notification-icon.svg',
        tag: 'test',
      })
    } else {
      // Fallback: use Notification API directly
      new Notification('Test Notification', { body: 'Push notifications are working correctly.' })
    }
  }

  const { data: unreadCount = 0 } = useUnreadAlertCount()

  const { data: recentAlerts = [] } = useQuery({
    queryKey: ['alerts', 'recent-dropdown'],
    queryFn:  () => listAlerts({ is_read: false, per_page: 5 }).then((r) => r.data),
    enabled:  alertsOpen,
    staleTime: 30_000,
  })

  const ColorModeIcon = COLOR_MODE_ICONS[colorMode]

  const toggleLanguage = () => {
    const next = i18n.language.startsWith('ar') ? 'en' : 'ar'
    // 1. Persist explicitly — this is what survives page reloads
    localStorage.setItem('i18n_language', next)
    // 2. Apply RTL/LTR direction to <html> immediately
    const root = document.documentElement
    root.setAttribute('dir', next === 'ar' ? 'rtl' : 'ltr')
    root.setAttribute('lang', next)
    root.classList.toggle('rtl', next === 'ar')
    // 3. Tell i18next (re-renders all useTranslation components)
    i18n.changeLanguage(next).catch(console.error)
  }

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface px-5 shadow-sm">
      {/* Mobile sidebar toggle */}
      <button
        onClick={toggleSidebar}
        className="rounded p-1.5 text-muted hover:bg-surface-overlay hover:text-muted-fg lg:hidden"
        aria-label="Toggle sidebar"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="flex-1" />

      {/* Language toggle */}
      <button
        onClick={toggleLanguage}
        className="rounded px-2 py-1 text-xs font-medium text-muted hover:bg-surface-overlay hover:text-muted-fg"
        title={t('common:language')}
      >
        {i18n.language === 'ar' ? 'EN' : 'عربي'}
      </button>

      {/* Theme colour picker */}
      <div className="relative">
        <button
          onClick={openThemePicker}
          className="rounded p-1.5 text-muted hover:bg-surface-overlay hover:text-muted-fg"
          title="Theme colour"
          aria-label="Change theme colour"
        >
          {/* Show a tiny circle with the current primary colour */}
          <span className="relative flex h-4 w-4 items-center justify-center">
            <Palette className="h-4 w-4" />
            <span
              className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border border-white"
              style={{ background: currentPrimary }}
            />
          </span>
        </button>

        {themePickerOpen && (
          <>
            <div className="fixed inset-0 z-[1999]" onClick={() => setThemePickerOpen(false)} />
            <div className="absolute end-0 z-[2000] mt-2 w-64 rounded-xl border border-border bg-surface shadow-2xl overflow-hidden">

              {/* Picker header */}
              <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                <span className="text-xs font-semibold text-muted-fg">Theme Colour</span>
                <div className="h-5 w-5 rounded-full border-2 border-white shadow" style={{ background: pickerColor }} />
              </div>

              {/* react-colorful gradient + hue slider */}
              <div className="p-3">
                <HexColorPicker
                  color={pickerColor}
                  onChange={setPickerColor}
                  style={{ width: '100%', height: '160px' }}
                />
              </div>

              {/* Hex input */}
              <div className="mx-3 mb-3 flex items-center gap-2 rounded-lg border border-border bg-surface-overlay px-3 h-9">
                <span
                  className="h-5 w-5 shrink-0 rounded-full border border-border"
                  style={{ background: pickerColor }}
                />
                <span className="text-xs text-muted">#</span>
                <HexColorInput
                  color={pickerColor}
                  onChange={setPickerColor}
                  className="flex-1 bg-transparent text-xs font-mono uppercase text-muted-fg outline-none"
                  placeholder="53C9C0"
                />
              </div>

              {/* Quick preset swatches */}
              <div className="mx-3 mb-3 flex gap-2">
                {['#53C9C0','#3B82F6','#8B5CF6','#F59E0B','#EF4444','#10B981'].map((c) => (
                  <button
                    key={c}
                    onClick={() => setPickerColor(c)}
                    className="h-6 w-6 rounded-full border-2 transition-transform hover:scale-110"
                    style={{
                      background: c,
                      borderColor: pickerColor.toLowerCase() === c.toLowerCase() ? '#fff' : 'transparent',
                      outline: pickerColor.toLowerCase() === c.toLowerCase() ? `2px solid ${c}` : 'none',
                    }}
                    title={c}
                  />
                ))}
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 border-t border-border p-3">
                <button
                  onClick={resetThemeColor}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border py-2 text-xs text-muted hover:bg-surface-overlay transition-colors"
                >
                  <RotateCcw className="h-3 w-3" />
                  Reset
                </button>
                <button
                  onClick={applyThemeColor}
                  className="flex flex-1 items-center justify-center rounded-lg py-2 text-xs font-semibold text-white transition-colors"
                  style={{ background: pickerColor }}
                >
                  Apply
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Map layer picker */}
      <div className="relative">
        <button
          onClick={() => setMapMenuOpen((v) => !v)}
          className="rounded p-1.5 text-muted hover:bg-surface-overlay hover:text-muted-fg"
          title="Map layer"
          aria-label="Change map layer"
        >
          <Map className="h-4 w-4" />
        </button>
        {mapMenuOpen && (
          <>
            <div className="fixed inset-0 z-[1999]" onClick={() => setMapMenuOpen(false)} />
            <div className="absolute end-0 z-[2000] mt-2 w-52 rounded-xl border border-border bg-surface shadow-2xl overflow-hidden">
              <div className="border-b border-border px-4 py-2.5">
                <p className="text-xs font-semibold text-muted-fg">Map Layer</p>
              </div>
              <div className="p-1.5 space-y-0.5">
                {MAP_LAYER_KEYS.map((key) => {
                  const tile = MAP_TILES[key]
                  return (
                    <button
                      key={key}
                      onClick={() => { setMapLayer(key as MapLayerKey); setMapMenuOpen(false) }}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                        mapLayer === key
                          ? 'bg-primary text-white font-semibold'
                          : 'text-muted-fg hover:bg-surface-overlay'
                      )}
                    >
                      <span className={cn(
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[10px] font-bold',
                        mapLayer === key ? 'bg-white/20' : 'bg-surface-overlay'
                      )}>
                        {tile.group === 'Google' ? 'G' : 'O'}
                      </span>
                      <span className="flex-1 text-left">{tile.label}</span>
                      {mapLayer === key && (
                        <span className="h-1.5 w-1.5 rounded-full bg-white" />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Color mode */}
      <div className="relative">
        <button
          onClick={() => setColorMenuOpen((v) => !v)}
          className="rounded p-1.5 text-muted hover:bg-surface-overlay hover:text-muted-fg"
          aria-label="Color mode"
        >
          <ColorModeIcon className="h-4 w-4" />
        </button>
        {colorMenuOpen && (
          <>
            <div
              className="fixed inset-0 z-[1999]"
              onClick={() => setColorMenuOpen(false)}
            />
            <div className="absolute end-0 z-[2000] mt-1 w-36 rounded border border-border bg-surface shadow-md">
              {(['light', 'dark', 'system'] as ColorMode[]).map((mode) => {
                const Icon = COLOR_MODE_ICONS[mode]
                return (
                  <button
                    key={mode}
                    onClick={() => { setColorMode(mode); setColorMenuOpen(false) }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-sm capitalize hover:bg-surface-overlay',
                      colorMode === mode && 'text-primary font-medium'
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {mode}
                  </button>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Push notification opt-in bell */}
      {'Notification' in window && (
        <div className="relative flex items-center">
          <button
            onClick={handleEnableNotifications}
            disabled={notifPerm === 'denied' || notifBusy}
            title={
              notifPerm === 'granted' ? 'Push notifications enabled — click to re-register'
              : notifPerm === 'denied' ? 'Notifications blocked — enable in browser settings'
              : 'Enable push notifications'
            }
            className={cn(
              'relative rounded p-1.5 transition-colors',
              notifPerm === 'granted'
                ? 'text-emerald-500 hover:bg-surface-overlay'
                : notifPerm === 'denied'
                ? 'cursor-not-allowed text-muted/40'
                : 'text-muted hover:bg-surface-overlay hover:text-muted-fg'
            )}
            aria-label="Push notifications"
          >
            {notifBusy
              ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent block" />
              : notifPerm === 'granted'
              ? <BellRing className="h-4 w-4" />
              : notifPerm === 'denied'
              ? <BellOff className="h-4 w-4" />
              : <Bell className="h-4 w-4" />
            }
            {notifPerm === 'default' && (
              <span className="absolute -end-0.5 -top-0.5 h-2 w-2 rounded-full bg-amber-400 ring-2 ring-surface" />
            )}
          </button>
          {/* Test button — only visible when permission is granted */}
          {notifPerm === 'granted' && (
            <button
              onClick={handleTestNotification}
              title="Send a test notification"
              className="ml-0.5 rounded p-1 text-[9px] font-bold text-emerald-500/60 hover:bg-surface-overlay hover:text-emerald-500 leading-none"
              aria-label="Test notification"
            >
              TEST
            </button>
          )}
        </div>
      )}

      {/* Alerts bell — dropdown */}
      <div className="relative">
        <button
          onClick={() => setAlertsOpen((v) => !v)}
          className="relative rounded p-1.5 text-muted hover:bg-surface-overlay hover:text-muted-fg"
          aria-label="Alerts"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-0.5 text-[10px] font-bold text-danger-fg">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {alertsOpen && (
          <>
            <div className="fixed inset-0 z-[1999]" onClick={() => setAlertsOpen(false)} />
            <div className="absolute end-0 z-[2000] mt-2 w-80 rounded-xl border border-border bg-surface shadow-2xl overflow-hidden">

              {/* Header */}
              <div className="border-b border-border bg-primary/5 px-4 py-2.5">
                <p className="text-xs font-semibold text-muted-fg">
                  {unreadCount > 0
                    ? `You Have ${unreadCount} new alert${unreadCount > 1 ? 's' : ''}`
                    : 'Notifications'}
                </p>
              </div>

              {/* Alert list */}
              {recentAlerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-8">
                  <Bell className="h-8 w-8 text-muted/40" />
                  <p className="text-xs text-muted">No alerts yet</p>
                </div>
              ) : (
                <div className="max-h-72 overflow-y-auto divide-y divide-border">
                  {recentAlerts.map((alert) => {
                    const p = alert.priority
                    const iconColor = p === 1 ? 'text-danger'  : p === 2 ? 'text-warning' : 'text-primary'
                    const bgColor   = p === 1 ? 'bg-danger/8'  : p === 2 ? 'bg-warning/8' : 'bg-primary/5'
                    const typeName = alert.alert_type?.name ?? (alert as any).alert_type_name ?? 'Alert'
                    const reg = (alert as any).vehicle_name ?? (alert as any).registration_number ?? alert.vehicle_id.slice(0, 10)
                    return (
                      <button
                        key={alert.id}
                        onClick={() => { setAlertsOpen(false); navigate('/alerts') }}
                        className={cn('flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-overlay', bgColor)}
                      >
                        <span className={cn('mt-0.5 shrink-0', iconColor)}>
                          {p === 3
                            ? <Info className="h-4 w-4" />
                            : <AlertTriangle className="h-4 w-4" />
                          }
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-xs font-semibold text-muted-fg">{typeName}</span>
                          <span className="block truncate text-[11px] text-muted">{reg}</span>
                          <span className="block truncate text-[11px] text-muted">
                            {new Date(alert.triggered_at).toLocaleString()}
                          </span>
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}

              {/* View More */}
              <div className="border-t border-border">
                <button
                  onClick={() => { setAlertsOpen(false); navigate('/alerts') }}
                  className="flex w-full items-center justify-center py-2.5 text-xs font-semibold text-primary hover:bg-primary/5 transition-colors"
                >
                  View More Alerts →
                </button>
              </div>

            </div>
          </>
        )}
      </div>

      {/* User menu */}
      <div className="relative">
        <button
          onClick={() => setUserMenuOpen((v) => !v)}
          className="flex items-center gap-2 rounded px-2 py-1 text-sm text-muted-fg hover:bg-surface-overlay"
        >
          <User2 className="h-4 w-4 shrink-0" />
          <span className="hidden max-w-32 truncate sm:block">{user?.name}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        </button>

        {userMenuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
            <div className="absolute end-0 z-20 mt-1 w-48 rounded border border-border bg-surface shadow-md">
              <Link
                to="/profile"
                onClick={() => setUserMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-muted-fg hover:bg-surface-overlay"
              >
                <User2 className="h-4 w-4" />
                {t('nav:profile')}
              </Link>
              <hr className="border-border" />
              <button
                onClick={() => { logout(); setUserMenuOpen(false) }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-fg hover:bg-surface-overlay"
              >
                <LogOut className="h-4 w-4" />
                {t('nav:logout')}
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  )
}
